'use client'

import { cn } from '@/shared/lib/utils'

export type FeedCardDensity = 'default' | 'compact'

const densityClass: Record<
  FeedCardDensity,
  {
    shell: string
    innerGap: string
    shadow: string
    ring: string
    beforeInset: string
    afterRadius: string
    interactiveHover: string
    interactiveActive: string
  }
> = {
  default: {
    shell: 'rounded-md p-2.5 sm:p-3',
    innerGap: 'gap-2 sm:gap-2.5',
    shadow:
      'shadow-[0_4px_0_0_rgba(0,0,0,0.48),0_10px_22px_-8px_rgba(0,0,0,0.48)]',
    ring: 'ring-0 ring-inset ring-white/10',
    beforeInset: 'before:inset-x-4 sm:before:inset-x-5',
    afterRadius: 'after:rounded-xl',
    interactiveHover:
      'hover:-translate-y-0.5 hover:shadow-[0_5px_0_0_rgba(0,0,0,0.48),0_12px_26px_-8px_rgba(0,0,0,0.5)]',
    interactiveActive:
      'active:translate-y-0 active:shadow-[0_3px_0_0_rgba(0,0,0,0.52),0_8px_18px_-6px_rgba(0,0,0,0.45)]'
  },
  compact: {
    shell: 'rounded-lg p-2',
    innerGap: 'gap-1.5',
    shadow:
      'shadow-[0_3px_0_0_rgba(0,0,0,0.52),0_6px_14px_-6px_rgba(0,0,0,0.42)]',
    ring: 'ring-1 ring-inset ring-white/8',
    beforeInset: 'before:inset-x-2.5',
    afterRadius: 'after:rounded-lg',
    interactiveHover:
      'hover:-translate-y-px hover:shadow-[0_3px_0_0_rgba(0,0,0,0.5),0_8px_16px_-6px_rgba(0,0,0,0.45)]',
    interactiveActive:
      'active:translate-y-0 active:shadow-[0_2px_0_0_rgba(0,0,0,0.55),0_4px_12px_-4px_rgba(0,0,0,0.4)]'
  }
}

export function FeedCardSurface({
  children,
  className,
  density = 'default',
  onClick,
  onKeyDown
}: {
  children: React.ReactNode
  className?: string
  density?: FeedCardDensity
  onClick?: () => void
  onKeyDown?: React.KeyboardEventHandler<HTMLDivElement>
}) {
  const interactive = Boolean(onClick)
  const d = densityClass[density]

  return (
    <div
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onClick={onClick}
      onKeyDown={e => {
        if (!interactive || !onClick) return
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick()
        }
        onKeyDown?.(e)
      }}
      className={cn(
        'relative flex flex-col overflow-hidden',
        d.shell,
        d.shadow,
        d.ring,
        'bg-[linear-gradient(168deg,#3d3d5c_0%,#2f2f48_35%,#252538_100%)]',
        'before:pointer-events-none before:absolute before:top-0 before:h-px before:bg-linear-to-r before:from-transparent before:via-white/20 before:to-transparent',
        d.beforeInset,
        'after:pointer-events-none after:absolute after:inset-0 after:shadow-[inset_0_1px_0_0_rgba(255,255,255,0.06)]',
        d.afterRadius,
        interactive &&
          cn(
            'cursor-pointer transition-[transform,box-shadow] duration-200',
            d.interactiveHover,
            d.interactiveActive
          ),
        className
      )}
    >
      <div className={cn('relative z-1 flex flex-col', d.innerGap)}>
        {children}
      </div>
    </div>
  )
}

export type FeedAccentWellSize = 'md' | 'sm'

export function FeedActivityAccentWell({
  children,
  className,
  size = 'md'
}: {
  children: React.ReactNode
  className?: string
  size?: FeedAccentWellSize
}) {
  return (
    <div
      className={cn(
        'flex shrink-0 items-center justify-center self-start',
        size === 'sm'
          ? 'size-8 rounded-md bg-[linear-gradient(180deg,#4a4a72_0%,#363652_48%,#2c2c44_100%)] shadow-[inset_0_1px_0_0_rgba(255,255,255,0.12),0_2px_0_0_rgba(0,0,0,0.38)]'
          : 'size-11 rounded-lg bg-[linear-gradient(180deg,#4f4f78_0%,#3a3a58_48%,#303048_100%)] shadow-[inset_0_1px_0_0_rgba(255,255,255,0.13),0_3px_0_0_rgba(0,0,0,0.4)]',
        'ring-1 ring-inset ring-white/10',
        className
      )}
    >
      {children}
    </div>
  )
}
